import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Chip, CircularProgress, Container, Paper, Tab, Tabs, Typography, makeStyles } from "@material-ui/core";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import RefreshIcon from "@material-ui/icons/Refresh";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useInbox } from "../../context/Inbox";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatDateTime, localTimeZone, toDate } from "../../utils/waitingTime";

const useStyles = makeStyles(theme => ({
  root: { paddingTop: 28, paddingBottom: 32, maxWidth: 1080 },
  header: { display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", marginBottom: 24 },
  heroIcon: { display: "grid", placeItems: "center", width: 58, height: 58, borderRadius: 18, color: theme.palette.primary.main, background: "rgba(16,185,129,.12)" },
  title: { fontWeight: 800, fontSize: "1.65rem", letterSpacing: "-.03em" },
  intro: { flex: 1, minWidth: 180 },
  subtitle: { color: theme.palette.text.secondary, fontSize: ".85rem", marginTop: 4 },
  panel: { border: `1px solid ${theme.custom.border}`, borderRadius: 18, overflow: "hidden", boxShadow: "none" },
  tools: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", padding: "8px 16px", gap: 8, borderBottom: `1px solid ${theme.custom.border}` },
  row: { display: "flex", gap: 16, padding: 22, position: "relative", borderBottom: `1px solid ${theme.custom.border}`, "&:last-child": { borderBottom: 0 }, [theme.breakpoints.down("xs")]: { padding: 16, gap: 10 } },
  unread: { background: theme.palette.type === "dark" ? "rgba(16,185,129,.06)" : "#F3FCF8", boxShadow: "inset 3px 0 #10B981" },
  icon: { width: 38, height: 38, borderRadius: 12, flexShrink: 0, display: "grid", placeItems: "center", background: "rgba(16,185,129,.12)", color: theme.palette.primary.main },
  body: { flex: 1, minWidth: 0 },
  rowTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  name: { fontSize: "1rem", fontWeight: 700, overflowWrap: "anywhere" },
  summary: { fontSize: ".9rem", margin: "9px 0 5px", overflowWrap: "anywhere" },
  meta: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", color: theme.palette.text.secondary, fontSize: ".77rem" },
  actions: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 },
  empty: { padding: "64px 24px", textAlign: "center", color: theme.palette.text.secondary, "& svg": { fontSize: 44, marginBottom: 12, opacity: .65 } },
  error: { padding: 18, color: theme.palette.error.main, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: 16, flexWrap: "wrap", borderTop: `1px solid ${theme.custom.border}` },
}));

export default function Inbox() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { unreadCount, revision, refresh } = useInbox();
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ notifications: [], count: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!user?.id) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    api.get("/inbox", { params: { pageNumber: page, unreadOnly: filter === "unread" } })
      .then(({ data }) => {
        if (!active) return;
        if (page > 1 && !data.notifications.length) { setPage(1); return; }
        setResult(data);
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user?.id, user?.companyId, filter, page, revision]);

  const markRead = async id => {
    setBusy(id || "all");
    try {
      await api.put(id ? `/inbox/${id}/read` : "/inbox/read-all");
      if (filter === "unread") setPage(1);
      refresh();
    } catch (err) { toastError(err); }
    finally { setBusy(null); }
  };

  return (
    <Container className={classes.root}>
      <div className={classes.header}>
        <div className={classes.heroIcon}><InboxOutlinedIcon style={{ fontSize: 30 }} /></div>
        <div className={classes.intro}>
          <Typography component="h1" className={classes.title}>Meu inbox</Typography>
          <Typography className={classes.subtitle}>Quem passou cada atendimento para você, com todo o histórico em um só lugar.</Typography>
        </div>
        <Chip label={`${unreadCount} não ${unreadCount === 1 ? "lido" : "lidos"}`} color={unreadCount ? "primary" : "default"} variant="outlined" />
      </div>
      <Paper className={classes.panel}>
        <div className={classes.tools}>
          <Tabs value={filter} onChange={(_, value) => { setFilter(value); setPage(1); }} indicatorColor="primary" textColor="primary" aria-label="Filtrar inbox">
            <Tab value="all" label="Todos os registros" />
            <Tab value="unread" label={<Badge color="primary" variant="dot" invisible={!unreadCount}>Não lidos</Badge>} />
          </Tabs>
          <div>
            <Button size="small" startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>Atualizar</Button>
            <Button size="small" startIcon={<DoneAllIcon />} onClick={() => markRead()} disabled={!unreadCount || busy !== null}>Marcar todos como lidos</Button>
          </div>
        </div>
        {error && <div role="alert" className={classes.error}>Não foi possível carregar seu inbox. <Button size="small" onClick={refresh}>Tentar novamente</Button></div>}
        {loading && result.notifications.length === 0 && <div className={classes.empty}><CircularProgress size={28} aria-label="Carregando inbox" /></div>}
        {!error && !loading && result.notifications.length === 0 && (
          <div className={classes.empty}>
            {filter === "unread" ? <DoneAllIcon /> : <InboxOutlinedIcon />}
            <Typography style={{ fontWeight: 700, marginBottom: 6 }}>{filter === "unread" ? "Tudo em dia" : "Seu histórico começa aqui"}</Typography>
            <Typography variant="body2">{filter === "unread" ? "Você já leu todos os registros recebidos." : "Os próximos atendimentos recebidos aparecerão aqui, mesmo quando você estiver offline."}</Typography>
          </div>
        )}
        <div aria-busy={loading}>
          {result.notifications.map(item => {
            const self = Number(item.actorId) === Number(user.id);
            const sentDate = toDate(item.createdAt);
            return <article key={item.id} className={`${classes.row} ${!item.readAt ? classes.unread : ""}`}>
              <div className={classes.icon}><SwapHorizIcon /></div>
              <div className={classes.body}>
                <div className={classes.rowTop}>
                  <Typography component="h2" className={classes.name}>{item.contactName || "Contato"}</Typography>
                  {!item.readAt && <Chip size="small" label="Novo" color="primary" variant="outlined" />}
                </div>
                <Typography className={classes.summary}>
                  <strong>{self ? "Você" : item.actorName || "Um atendente"}</strong>{self ? " assumiu este atendimento." : " transferiu este atendimento para você."}
                </Typography>
                <div className={classes.meta}>
                  <span>{item.previousUserName || "Fila de espera"} → {item.recipientName || user.name}</span>
                  <span aria-hidden="true">·</span>
                  <span>Atendimento #{item.ticketId || "removido"}</span>
                </div>
                <div className={classes.meta} style={{ marginTop: 6 }}>
                  <time dateTime={sentDate ? sentDate.toISOString() : undefined} title={`Horário local · ${localTimeZone()}`}>{formatDateTime(item.createdAt)}</time>
                </div>
                <div className={classes.actions}>
                  {item.ticketId && <Button size="small" component={Link} to={`/tickets/${item.ticketId}`} variant="outlined" color="primary" endIcon={<ArrowForwardIcon />}>Abrir atendimento</Button>}
                  {!item.readAt && <Button size="small" onClick={() => markRead(item.id)} disabled={busy !== null} startIcon={<DoneAllIcon />}>{busy === item.id ? "Salvando…" : "Marcar como lido"}</Button>}
                </div>
              </div>
            </article>;
          })}
        </div>
        <div className={classes.footer}>
          <Typography variant="caption" color="textSecondary">{result.count} {result.count === 1 ? "registro" : "registros"} · Horário local ({localTimeZone()})</Typography>
          <div>
            <Button size="small" disabled={page === 1 || loading} onClick={() => setPage(value => value - 1)}>Anterior</Button>
            <Typography variant="caption" component="span">Página {page}</Typography>
            <Button size="small" disabled={!result.hasMore || loading} onClick={() => setPage(value => value + 1)}>Próxima</Button>
          </div>
        </div>
      </Paper>
    </Container>
  );
}
